import type { FacilityNode } from "@arc/contracts";
import { FACILITY_PERMISSION_LEVELS } from "@arc/contracts";

/**
 * Is THIS session a shop-floor kiosk? (ADR-036 D3.)
 *
 * The legacy decided by comparing `preferred_username` against a per-realm constant `genericUsername` — a string
 * literal that had to be configured per realm and that silently stopped working the moment the account was renamed.
 * Here it is the **role**, which is already in the verified token, already a real `role` row with real facility
 * grants, and needs no per-realm config: a realm without the role simply has no kiosk, so no feature flag either.
 *
 * **This is a NAVIGATION decision, not a security boundary** (D5). Nothing here widens or narrows what the caller may
 * read — the authz engine does that on the server for every request, kiosk or not. All this decides is which screen
 * the device lands on, which is exactly what the legacy's cookie decided too.
 */
export const KIOSK_ROLE = "GenericUser";

/**
 * Every tier ABOVE plain `view`. Derived from the shared ladder rather than re-typed, so a sixth level added to
 * ADR-004 cannot quietly fall through this gate as "not administrative".
 */
const ABOVE_VIEW: ReadonlySet<string> = new Set(FACILITY_PERMISSION_LEVELS.filter((l) => l !== "view"));

/**
 * Does the session hold ANY capability beyond reading? Capability, not role names: realms name their roles freely,
 * so a composite role or a second grant would slip past a name list, while the permitted tree cannot lie about what
 * the caller can actually do.
 */
export function hasCapabilityAboveView(flat: readonly FacilityNode[]): boolean {
  return flat.some((f) => f.permission !== null && ABOVE_VIEW.has(f.permission));
}

/**
 * Kiosk mode requires the role AND no administrative capability (D3 guard 1) — **never trap a human**. A person who
 * also happens to hold `GenericUser` (a composite role, or an admin testing the kiosk on their own account) keeps the
 * normal app: the chrome-less lockdown applies only to a session that is ONLY a kiosk.
 *
 * A kiosk-only session with an empty tree is still a kiosk — it has no grants yet, which is a configuration problem
 * the landing screen states plainly rather than a reason to drop the device into the authoring UI.
 */
export function isKioskOnlySession(roles: readonly string[], flat: readonly FacilityNode[]): boolean {
  if (!roles.includes(KIOSK_ROLE)) return false;
  return !hasCapabilityAboveView(flat);
}
